import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/lib/utils';

const alertVariants = cva(
  `
    relative grid w-full grid-cols-[0_1fr] items-center gap-y-0.5 rounded-lg
    border p-16 text-sm
    has-[>svg]:grid-cols-[--spacing(16)_1fr] has-[>svg]:gap-x-8
    [&>svg]:size-16
  `,
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive:
          `
            border-burgundy-lighten-30 bg-burgundy-lighten-80
            text-burgundy-darken-50
            [&>svg]:text-burgundy-darken-10
          `,
        warning:
          'border-gold-brand-gold-lighten-10 bg-gold-brand-gold-lighten-80 text-gold-brand-gold-darken-70',
        info: 'border-baby-blue-lighten-15 bg-baby-blue-lighten-60 text-baby-blue-darken-50',
        success:
          'border-none bg-feedback-green-lighten-75 text-feedback-green-darken-40 [&>svg]:text-feedback-green-darken-15',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      className={cn(alertVariants({ variant }), className)}
      data-slot="alert"
      role="alert"
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight',
        className,
      )}
      data-slot="alert-title"
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        `
          col-start-2 grid justify-items-start gap-1 text-sm
          [&_p]:leading-relaxed
        `,
        className,
      )}
      data-slot="alert-description"
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
